import type { MetaResponse } from '../api/client';
import { SITE_URL } from './site';

/**
 * What each kind of message is, in words someone answering it can act on.
 *
 * The API's form registry says who a message routes to, how fast it wants a
 * reply and what finishes it (`/meta` forms: route, sla, primary). It does not
 * say what the thing *is* — "Status pings" and "Preferences" meant nothing to
 * the people clearing the queue, and the table offered no way to find out.
 * This is that missing half: a plain name, the question as the sender saw it
 * on the public site, what it is and is not, and where it comes from.
 *
 * The descriptions follow the public site's own copy for each form
 * (ENACT-MAIN-WEBSITE `src/data/forms.ts`: purpose, plainLanguage), rewritten
 * for the person replying rather than the person sending. Presentation, so it
 * lives here like `columns.ts` does: a form the API adds without an entry
 * still lists, under its `/meta` label, with a plain fallback line.
 */

export type MessageGroupKey = 'anyone' | 'clinicians' | 'in-touch' | 'partners' | 'trainees';

export interface MessageGroup {
  key: MessageGroupKey;
  /** The rail heading: who sends these. */
  label: string;
}

/** Messages grouped by who sends them — the question a coordinator starts from. */
export const MESSAGE_GROUPS: readonly MessageGroup[] = [
  { key: 'anyone', label: 'Anyone' },
  { key: 'clinicians', label: 'Clinicians' },
  { key: 'in-touch', label: 'People already in touch' },
  { key: 'partners', label: 'Partners & press' },
  { key: 'trainees', label: 'Trainees' },
];

export interface MessageGuide {
  group: MessageGroupKey;
  /** The name on this screen: what the message is, not the form's code. */
  name: string;
  /** One of them, for a single row or an open message. */
  one: string;
  /** Under the name in the list of types: what the sender wants, in a few words. */
  hint: string;
  /** The form's title on the public site — what the sender clicked. */
  asked: string;
  /** What it is, in a sentence or two. */
  what: string;
  /** What it is not, where the two are easy to confuse. */
  not?: string;
  /** Something the system already did before anyone opened it. */
  already?: string;
  /** Why replying by email would go against what the sender asked for. */
  noReply?: string;
  /** The public pages it is sent from; empty while the site does not offer it. */
  from: ReadonlyArray<{ label: string; path: string }>;
}

export const MESSAGE_GUIDE: Record<string, MessageGuide> = {
  inquiry: {
    group: 'anyone',
    name: 'General inquiries',
    one: 'General inquiry',
    hint: 'Questions that aren’t about joining a study',
    asked: 'Send us a message',
    what:
      'A question for the lab that is not about joining a study: research, collaboration, training, press, giving, directions or the website. The sender picks a topic, and the topic is how it is routed.',
    not:
      'Not a way into a study and not for medical advice. Someone writing about their own health should be pointed to the intake call request.',
    from: [
      { label: 'Contact page', path: '/contact' },
      { label: 'General inquiry tab', path: '/intake#inquiry' },
    ],
  },
  'pre-referral-fit': {
    group: 'clinicians',
    name: 'Fit questions',
    one: 'Fit question',
    hint: 'Would this kind of patient fit a study?',
    asked: 'Is this the kind of patient I should refer?',
    what: 'A clinician asks whether a kind of patient fits one of our studies, before referring anyone.',
    not: 'No patient is named and nothing is referred. If they want us to contact someone, they should send a referral.',
    from: [{ label: 'Refer a Patient', path: '/forms/pre-referral-fit' }],
  },
  'referral-status': {
    group: 'clinicians',
    name: 'Referral status checks',
    one: 'Referral status check',
    hint: 'Did you get my referral?',
    asked: 'Did you get my referral?',
    what: 'A clinician who already sent a referral asks whether it arrived, or what we can tell them about it.',
    not: 'Starts no new referral. Share only what they and the patient already agreed to.',
    from: [{ label: 'Refer a Patient', path: '/forms/referral-status' }],
  },
  'clinician-alerts': {
    group: 'clinicians',
    name: 'Study opening alerts',
    one: 'Study opening alert',
    hint: 'Email me when a study opens',
    asked: 'Email me when a study starts taking inquiries',
    what:
      'A clinician asks to be emailed when the studies they picked start taking inquiries, and, if they ticked it, when those studies close.',
    not: 'Not a referral, and not a place on a study list.',
    from: [{ label: 'Refer a Patient', path: '/forms/clinician-alerts' }],
  },
  'stop-contacting-me': {
    group: 'in-touch',
    name: 'Stop-contact requests',
    one: 'Stop-contact request',
    hint: 'Stop emailing and calling me',
    asked: 'Stop contacting me',
    what:
      'Someone who wrote to us before wants no more emails or calls, and the contact details they gave removed.',
    not: 'Does not take anyone out of a study they joined. That goes through their study team.',
    already:
      'It lands as Stopped. When the address matches someone we know, their contact is already marked do-not-contact and their open follow-ups are closed. Check any other address they list.',
    noReply: 'they asked us to stop using that address.',
    from: [{ label: 'Privacy page', path: '/forms/stop-contacting-me' }],
  },
  'how-to-reach-me': {
    group: 'in-touch',
    name: 'Contact preferences',
    one: 'Contact preferences',
    hint: 'How and when to reach me',
    asked: 'How to reach me',
    what: 'Someone who wrote to us before says how and when to reach them, and in which language.',
    not: 'Does not start a new inquiry or decide whether a study fits.',
    from: [{ label: 'Privacy page', path: '/forms/how-to-reach-me' }],
  },
  'access-interpreter': {
    group: 'in-touch',
    name: 'Access & interpreter needs',
    one: 'Access & interpreter needs',
    hint: 'Interpreter or access help for a visit',
    asked: 'Access / interpreter request',
    what: 'Someone coming in for a visit asks for an interpreter or access help, so the visit can be planned around it.',
    not: 'Mobility needs and notes are health-adjacent, so they stay inside the message rather than in the list.',
    from: [],
  },
  partnership: {
    group: 'partners',
    name: 'Partnership & giving',
    one: 'Partnership & giving',
    hint: 'Gifts and research partnerships',
    asked: 'Partnership / philanthropy inquiry',
    what: 'Someone wants to talk about a gift, a research partnership or industry work.',
    not: 'A conversation, not a donation: the form takes no payment. Gifts go through Yale GiveToday.',
    from: [{ label: 'Support the research', path: '/forms/partnership' }],
  },
  media: {
    group: 'partners',
    name: 'Media & press',
    one: 'Media inquiry',
    hint: 'Interviews, fact-checks, study status',
    asked: 'Covering the research?',
    what: 'A journalist asks for an interview, a fact-check or a study’s status, usually against a deadline.',
    not: 'We do not comment on individual people in our studies, or on unpublished results.',
    from: [{ label: 'Media & press page', path: '/media' }],
  },
  fellowship: {
    group: 'trainees',
    name: 'Fellowship & training',
    one: 'Fellowship & training',
    hint: 'Interest in training with the lab',
    asked: 'Fellowship / training interest',
    what: 'A resident, fellow or student says they are interested in training with the lab.',
    not: 'An interest note, not an application. Training does not certify independent psychedelic practice.',
    from: [{ label: 'Fellowship & training page', path: '/training' }],
  },
  'resident-rotation': {
    group: 'trainees',
    name: 'Resident rotations',
    one: 'Resident rotation',
    hint: 'Rotating with the lab',
    asked: 'Resident rotation request',
    what: 'A resident asks to rotate with the lab, with their program, year and dates.',
    from: [],
  },
  'student-observer': {
    group: 'trainees',
    name: 'Students & observers',
    one: 'Student / observer',
    hint: 'Observing, electives and theses',
    asked: 'Student / observer interest',
    what: 'A student asks to observe, or to do an elective or thesis with the lab.',
    from: [],
  },
};

/** The message forms: the general inquiry and every request, in rail order. */
export function messageForms(meta: MetaResponse): string[] {
  const requests = meta.formGroups?.requests ?? [];
  const known = Object.keys(MESSAGE_GUIDE);
  // The guide's order first (grouped by sender), then anything the API added
  // that this file has not heard of yet.
  return [
    ...known.filter((k) => k === 'inquiry' || requests.includes(k as never)),
    ...requests.filter((k) => !known.includes(k)),
  ];
}

/**
 * Whether the public site offers this form. A type it does not offer is left
 * out of the list until a message of that type turns up, so the list is not
 * padded with sections that can only ever be empty.
 */
export const offeredOnSite = (form: string): boolean =>
  MESSAGE_GUIDE[form] ? MESSAGE_GUIDE[form].from.length > 0 : true;

export const isMessageForm = (meta: MetaResponse, form: string): boolean =>
  messageForms(meta).includes(form);

/** A form's name on this screen, falling back to the API's label. */
export function messageName(meta: MetaResponse, form: string): string {
  return MESSAGE_GUIDE[form]?.name ?? meta.forms[form as keyof MetaResponse['forms']]?.label ?? form;
}

/** One message's type, for a row or an open message: "Fit question". */
export function messageOne(meta: MetaResponse, form: string): string {
  return MESSAGE_GUIDE[form]?.one ?? meta.forms[form as keyof MetaResponse['forms']]?.label ?? form;
}

/** A form's group; an unknown one sits with the anyone-can-send messages. */
export const groupOf = (form: string): MessageGroupKey => MESSAGE_GUIDE[form]?.group ?? 'anyone';

export const siteLink = (path: string): string => `${SITE_URL}${path}`;

/**
 * The statuses a message can sensibly be in. The ladder is shared with the
 * participant path, which is why a media inquiry could be marked "Enrolled";
 * a message is new, being handled, waiting on the sender, done, or stopped.
 */
export const MESSAGE_STATUSES: readonly string[] = ['received', 'under-review', 'hold', 'done', 'stopped'];

/**
 * The message statuses for a select or a filter, in the order a message moves
 * through them — plus the row's own, last, if it is somewhere else.
 */
export function messageStatusOptions<T extends { key: string }>(statuses: readonly T[], current?: string): T[] {
  const inOrder = MESSAGE_STATUSES.flatMap((k) => statuses.filter((s) => s.key === k));
  const own = statuses.find((s) => s.key === current && !MESSAGE_STATUSES.includes(s.key));
  return own ? [...inOrder, own] : inOrder;
}
